import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import JsonLd from "../components/JsonLd";
import OffsiteEnquiryForm from "../components/OffsiteEnquiryForm";
import {
  Mountain,
  Home,
  Users,
  Mic,
  MessageCircle,
  ArrowRight,
  Check,
  Wifi,
  Waves,
  Utensils,
  Sparkles,
  Plane,
  Sun,
  Building2,
  Rocket,
  Camera,
  Brain,
} from "lucide-react";
import { pic } from "../lib/pic";

const WHATSAPP_OFFSITE = "https://wa.me/34632628051?text=Hi%20Steve!%20I'm%20interested%20in%20booking%20Savi%20for%20a%20company%20off-site.";

const OffsitePage = () => {
  const gallery = [
    { name: "terrace-lunch", alt: "Team lunch on the Savi terrace" },
    { name: "pool", alt: "Pool and terrace at the villa" },
    { name: "yoga-savi-1", alt: "Morning yoga on the terrace" },
    { name: "app-events", alt: "Community gathering at Savi" },
  ];

  const included = [
    { icon: Home, title: "16 private rooms", desc: "The whole villa, just your team" },
    { icon: Wifi, title: "Coworking + Starlink", desc: "Dual-screen desks, ultra-fast WiFi" },
    { icon: Utensils, title: "Chef-cooked meals", desc: "Fresh, local, made on-site" },
    { icon: Sparkles, title: "Wellness programme", desc: "Yoga, meditation & sound therapy" },
    { icon: Mountain, title: "Guided sierra hikes", desc: "Straight from the door" },
    { icon: Waves, title: "Pool, gym & sky terrace", desc: "200m² of open air" },
    { icon: Camera, title: "Recording studio", desc: "4K multi-cam for content" },
    { icon: Plane, title: "Airport transfers", desc: "25 min from Valencia, on request" },
  ];

  const perfectFor = [
    { icon: Users, label: "Team retreats", detail: "Reconnect off the grid" },
    { icon: Building2, label: "Leadership off-sites", detail: "Think without noise" },
    { icon: Rocket, label: "Product sprints", detail: "Deep work, then ship" },
    { icon: Brain, label: "Founder masterminds", detail: "Small, sharp, focused" },
  ];

  const week = [
    { day: "Arrive", text: "Land in Valencia, transfer up to the sierra. Settle in, dinner on the terrace." },
    { day: "Mornings", text: "Optional yoga, then long, uninterrupted deep-work blocks in the coworking space." },
    { day: "Afternoons", text: "Workshops, strategy sessions — or a guided hike, pool, and the sky terrace." },
    { day: "Evenings", text: "Chef-cooked dinners, sound therapy, fire pit. No bar, no hangovers — just the team." },
    { day: "Leave", text: "A team that actually rested, actually connected, and actually got the work done." },
  ];

  const offsiteSchemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Savi Coliving Company Off-sites",
    "serviceType": "Corporate retreat & team off-site venue",
    "description":
      "Book the entire Savi villa for your company off-site near Valencia, Spain. Up to 16 guests across 16 private rooms, coworking, chef-cooked meals, wellness programme and a recording studio — an alcohol-free, focus-first venue.",
    "image": "https://www.savicoliving.com/images/villa-aerial.jpg",
    "provider": { "@type": "Organization", "name": "Savi Coliving", "url": "https://www.savicoliving.com" },
    "areaServed": { "@type": "Place", "name": "Valencia, Spain" },
    "offers": { "@type": "Offer", "availability": "https://schema.org/LimitedAvailability", "url": "https://www.savicoliving.com/offsites" },
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Company Off-sites | Savi Coliving Valencia</title>
        <meta
          name="description"
          content="Book the whole Savi villa for your company off-site near Valencia. Up to 16 guests, 16 private rooms, coworking, a chef, wellness & a studio — an alcohol-free, focus-first venue."
        />
        <meta property="og:title" content="Company Off-sites at Savi Coliving" />
        <meta property="og:description" content="Bring your whole team to the mountains. Exclusive whole-house buy-out near Valencia — coworking, chef, wellness, studio." />
        <meta property="og:image" content="https://www.savicoliving.com/images/villa-aerial.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.savicoliving.com/offsites" />
      </Helmet>
      <JsonLd id="offsites" data={offsiteSchemaData} />

      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="relative pt-16">
        <div className="h-[46vh] sm:h-[56vh] relative overflow-hidden">
          <img {...pic("villa-aerial")} fetchpriority="high" decoding="async" alt="Aerial view of the Savi villa in the Sierra Calderona" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] via-[#FBF7F1]/40 to-black/25"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-[#C85A38] font-label text-[11px] sm:text-xs tracking-[0.28em] uppercase mb-2">Company off-sites</p>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2">Bring your whole team to the mountains.</h1>
              <p className="text-[#2E2A26]/75 text-sm sm:text-base max-w-2xl">
                Book the entire Savi villa for your off-site — 16 private rooms, coworking, a chef and a programme built around focus, not hangovers. Just your team, 25 minutes from Valencia.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* CTA / trust strip */}
      <div className="bg-[#FBF7F1] py-6 px-4 border-b border-stone-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/60 text-sm text-center sm:text-left">
            <span className="font-medium text-[#2E2A26]">Up to 16 guests · Exclusive whole-house buy-out · Alcohol-free</span>
          </p>
          <a href="#offsite-enquiry" data-testid="hero-offsite-cta">
            <Button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-8 py-5 text-sm font-medium tracking-wide">
              Request a custom quote <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>

      {/* Why Savi */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase text-center mb-3">Why Savi</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-3">An off-site your team will actually remember</h2>
          <p className="text-[#2E2A26]/60 text-sm text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            Most corporate retreats end at the hotel bar. Savi is different by design — a calm, alcohol-free villa where people rest properly, connect honestly and do the best work of the quarter.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Home, title: "The whole house, just you", body: "An exclusive buy-out. No other guests, no shared spaces — the villa, the pool and the programme are entirely yours." },
              { icon: Brain, title: "A clear head", body: "Alcohol-free by design, so mornings start sharp and the conversations that matter actually happen." },
              { icon: Sun, title: "Everything on-site", body: "Coworking, pool, gym, sky terrace and a studio — no minibus shuttles between a hotel and a venue." },
              { icon: Sparkles, title: "Done-for-you", body: "Yoga, guided hikes, sound therapy and chef-cooked meals, all handled by Steve and the team. You just show up." },
            ].map((item, i) => (
              <div key={i} className="bg-[#F4ECE2] rounded-2xl p-6 flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#2E2A26] font-serif text-lg mb-1">{item.title}</p>
                  <p className="text-[#2E2A26]/55 text-sm leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-2 sm:px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 gap-2">
          {gallery.map((img, i) => (
            <img key={i} loading="lazy" decoding="async" {...pic(img.name, { sizes: "(min-width: 1024px) 512px, 50vw", fallback: 480 })} alt={img.alt} className="w-full h-40 sm:h-56 object-cover rounded-lg" />
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase text-center mb-3">All included</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-8">One price, the whole experience</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {included.map((item, i) => (
              <div key={i} className="bg-[#F4ECE2] rounded-xl p-4 text-center flex flex-col items-center">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center mb-2.5">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <p className="text-[#2E2A26] text-sm font-medium mb-0.5">{item.title}</p>
                <p className="text-[#2E2A26]/55 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect for */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase text-center mb-3">Perfect for</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-8">Small teams that want to go deep</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {perfectFor.map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center p-5 bg-[#FBF7F1] rounded-xl">
                <item.icon className="w-5 h-5 text-[#C85A38] mb-2.5" />
                <p className="text-[#2E2A26] text-sm font-medium mb-0.5">{item.label}</p>
                <p className="text-[#2E2A26]/55 text-xs">{item.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#2E2A26]/55 text-xs mt-6">
            Also great for creator brand shoots, wellness weeks and founder mastermind groups.
          </p>
        </div>
      </section>

      {/* A week at Savi */}
      <section className="py-14 px-4 bg-[#2E2A26]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#C89B7B] font-label text-xs tracking-[0.2em] uppercase text-center mb-3">A week at Savi</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-white text-center mb-10">Shaped around your goals — not a fixed package</h2>
          <div className="space-y-4">
            {week.map((row, i) => (
              <div key={i} className="flex gap-4 sm:gap-6 items-start">
                <div className="w-24 sm:w-28 flex-shrink-0 pt-1">
                  <span className="font-label text-[11px] tracking-[0.2em] uppercase text-[#C89B7B]">{row.day}</span>
                </div>
                <p className="text-white/70 text-sm leading-relaxed border-l border-white/10 pl-4 sm:pl-6 flex-1">{row.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href="#offsite-enquiry" data-testid="week-offsite-cta">
              <Button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-6 text-sm font-medium">
                Plan your off-site <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-4">Already tested</p>
          <p className="font-serif text-xl sm:text-2xl text-[#2E2A26] leading-relaxed mb-4">
            &ldquo;We recently hosted a team for their corporate off-site — a proper pivot test — and the house worked beautifully. Focused mornings, real conversations, and everyone left better than they arrived.&rdquo;
          </p>
          <p className="text-[#2E2A26]/55 text-xs">Steve · Savi Coliving</p>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="offsite-enquiry" className="py-14 px-4 bg-[#F4ECE2] scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <OffsiteEnquiryForm />
          <div className="text-center mt-8">
            <p className="text-[#2E2A26]/50 text-sm mb-4">Prefer to talk it through first?</p>
            <a href={WHATSAPP_OFFSITE} target="_blank" rel="noopener noreferrer" data-testid="offsite-whatsapp-cta">
              <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-10 py-6 text-sm font-medium">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Steve
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default OffsitePage;
